import { useLazyCartSearchQuery } from '@api/cart.api';
import { CartSearchResponseData } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import { Progress, QuickSearchResultsItem } from '@components';
import { useCartContext } from '@context/CartContext';
import { Search } from '@mui/icons-material';
import {
  Alert,
  Box,
  ClickAwayListener,
  Collapse,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  styled,
} from '@mui/material';
import { colors } from '@themes/colors';
import { breakpoints } from '@themes/theme.helpers';
import { t } from '@translations';
import { ChangeEvent, useEffect, useState } from 'react';
import { useDebounce } from 'usehooks-ts';

const CartSearchBox = styled(Box)`
  margin-bottom: 16px;
  background-color: ${colors.background.white};
  ${breakpoints.up('lg')} {
    margin-bottom: 48px;
  }
`;

export const CartSearch = () => {
  const [currentSearchValue, setCurrentSearchValue] = useState<string>('');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const debouncedSearchValue = useDebounce(currentSearchValue, 1000);
  const [performSearch, results] = useLazyCartSearchQuery();
  const { focusOnInput, setFocusOnInput } = useCartContext();

  const changeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    setCurrentSearchValue(e.target.value);
  };

  // perform search on query string update
  useEffect(() => {
    if (debouncedSearchValue.length > 2) {
      setIsOpen(true);
      performSearch({ query: debouncedSearchValue });
    } else {
      setIsOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchValue]);

  useEffect(() => {
    setFocusOnInput(false);
  }, [setFocusOnInput]);

  const { isLoading, isFetching, isSuccess } = results;
  const isWorking = isLoading || isFetching;
  const data = debouncedSearchValue.length > 2 ? results.currentData?.data : {};

  const onFocus = () => {
    if (debouncedSearchValue.length > 2) {
      setIsOpen(true);
    }
  };

  return (
    <ClickAwayListener onClickAway={() => setIsOpen(false)}>
      <CartSearchBox>
        <TextField
          value={currentSearchValue}
          fullWidth
          placeholder={t(TextCodes.Product.Cart.SearchPlaceholder)}
          label={t(TextCodes.Product.Cart.SearchLabel)}
          onChange={changeHandler}
          onFocus={onFocus}
          autoFocus={focusOnInput}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <IconButton aria-label='toggle password visibility' edge='end'>
                  <Search />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Collapse in={isOpen}>
          <CartSearchResults data={data} isWorking={isWorking} isSuccess={isSuccess} />
        </Collapse>
      </CartSearchBox>
    </ClickAwayListener>
  );
};

export type CartSearchResultsProps = {
  data?: CartSearchResponseData;
  isWorking?: boolean;
  isSuccess?: boolean;
};

export const CartSearchResults = ({ data = {}, isWorking, isSuccess }: CartSearchResultsProps) => {
  const hasNoResults =
    isSuccess &&
    !isWorking &&
    data?.products?.items?.length === 0 &&
    data?.spareParts?.items?.length === 0;

  if (isWorking) {
    return (
      <Box p={2} textAlign='center'>
        <Progress />
      </Box>
    );
  }

  return (
    <Box>
      {hasNoResults && (
        <Box p={2} textAlign='center'>
          <Alert severity='info'>{t(TextCodes.Search.NoResultsInSection)}</Alert>
        </Box>
      )}
      {!hasNoResults ? (
        <Grid container spacing={4} p={0}>
          {data?.products?.items?.length ? (
            <Grid item xs={12} lg={data?.spareParts?.items?.length ? 6 : 12}>
              {data?.products?.items?.map((item) => (
                <QuickSearchResultsItem
                  key={item.code}
                  item={item}
                  showPrices
                  showImages
                  showProductNo
                />
              ))}
            </Grid>
          ) : null}
          {data?.spareParts?.items?.length ? (
            <Grid
              item
              xs={12}
              lg={data?.products?.items?.length ? 6 : 12}
              sx={{
                paddingTop: {
                  xs: data?.products?.items?.length ? '0 !important' : '32px',
                  lg: '32px !important',
                },
              }}
            >
              {data?.spareParts?.items?.map((item) => (
                <QuickSearchResultsItem
                  key={item.code}
                  item={item}
                  showPrices
                  showImages
                  showProductNo
                />
              ))}
            </Grid>
          ) : null}
        </Grid>
      ) : null}
    </Box>
  );
};
